import { useMutation, useQueryClient } from '@tanstack/react-query';

import { createLabel as createLabelRequest } from '@/lib/api/generated/api';
import type { LabelColor, LabelView } from '@/lib/api/generated/model';
import { workspaceKeys } from '@/features/workspaces';
import { CreateLabelError, classifyCreateLabelFailure } from '../errors';

export interface CreateLabelInput {
  workspaceId: string;
  name: string;
  /**
   * Settings' create dialog chooses one (spec 228); the task picker sends none
   * and lets the backend assign the least-used slot.
   */
  color?: LabelColor;
}

/**
 * Define a new label in the workspace's vocabulary.
 *
 * Sends no colour unless one is given: the backend then assigns the least-used
 * slot, so creating a label from a task is a one-field act — colour is not a
 * decision anyone wants at the moment they are trying to type a name. Settings'
 * dialog is the place that decision is made, and passes `color`.
 *
 * Invalidates rather than merging, unlike `useReplaceTaskLabels`. A new label
 * belongs to the registry, which every surface reads from the aggregate; there
 * is no single cached entity to patch, and creation happens once per new word
 * rather than on every toggle.
 *
 * A refusal rejects with a `CreateLabelError` carrying the classified reason,
 * which is what lets the picker's create row say "that name is taken" rather
 * than "something went wrong". `customFetch` resolves for a 4xx as much as for
 * a 201, so the status is checked here — it is also what narrows `data` from
 * `LabelView | Error` to the one this function returns.
 */
export function useCreateLabel() {
  const queryClient = useQueryClient();

  return useMutation<LabelView, Error, CreateLabelInput>({
    mutationFn: async ({ workspaceId, name, color }) => {
      const response = await createLabelRequest(
        workspaceId,
        color === undefined ? { name } : { name, color },
      );
      if (response.status !== 201) {
        throw new CreateLabelError(classifyCreateLabelFailure(response.data));
      }
      return response.data;
    },
    onSuccess: (_label, { workspaceId }) => {
      void queryClient.invalidateQueries({
        queryKey: workspaceKeys.detail(workspaceId),
      });
    },
  });
}
